'use client'

import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, Play, ShoppingBag, Store } from 'lucide-react'
import { Button } from '@/core/components/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/core/components/dialog'
import { ShopLink } from '@/features/auth/components/shop-link.client'
import { resolveShopImageUrl } from '@/features/shop/lib/shop-image'
import { shopClipTitle } from '@/features/shop/models/shop-clip.model'
import type { ShopClip } from '@/features/shop/types/shop.types'
import { routes } from '@/shared/routes'

interface ClipViewerDialogProps {
  clips: ShopClip[]
  activeIndex: number | null
  onOpenChange: (open: boolean) => void
  onChange: (index: number) => void
}

/**
 * Plays one Shop clip in place, with previous/next across the row. The video
 * is the signed URL the Shop handed over with the clip; when there is none,
 * the viewer points at the clip in the Shop instead.
 */
export function ClipViewerDialog({ clips, activeIndex, onOpenChange, onChange }: ClipViewerDialogProps) {
  const clip = activeIndex === null ? null : (clips[activeIndex] ?? null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [failed, setFailed] = useState(false)
  const [productImageFailed, setProductImageFailed] = useState(false)

  useEffect(() => {
    videoRef.current?.pause()
    setFailed(false)
    setProductImageFailed(false)
  }, [clip])

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) videoRef.current?.pause()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  if (!clip || activeIndex === null) return null
  const videoSrc = failed ? null : clip.videoUrl
  const productImage = resolveShopImageUrl(clip.product?.images[0])
  const title = clip.caption ?? clip.product?.title ?? `A clip from ${clip.business?.name ?? 'the Shop'}`

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-4xl gap-0 overflow-hidden rounded-lg p-0 sm:max-h-[calc(100dvh-3rem)]">
        <div className="grid max-h-[calc(100dvh-1rem)] min-h-0 grid-rows-[minmax(220px,48dvh)_minmax(0,1fr)] sm:max-h-[calc(100dvh-3rem)] md:grid-cols-[minmax(280px,0.78fr)_minmax(300px,1fr)] md:grid-rows-1">
          <div className="relative flex min-h-0 items-center justify-center bg-navy md:min-h-[min(76dvh,680px)]">
            {videoSrc ? (
              <video
                ref={videoRef}
                key={videoSrc}
                src={videoSrc}
                controls
                muted
                playsInline
                autoPlay
                preload="metadata"
                onError={() => setFailed(true)}
                className="h-full max-h-[72dvh] w-full object-contain"
                aria-label={shopClipTitle(clip)}
              />
            ) : (
              <div className="flex flex-col items-center gap-3 px-8 text-center text-navy-foreground">
                <Play className="h-10 w-10" />
                <p className="text-sm">This clip could not be loaded here.</p>
                <Button asChild variant="secondary">
                  <ShopLink href={clip.clipUrl}>Watch in Shop</ShopLink>
                </Button>
              </div>
            )}
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label="Previous clip"
              disabled={activeIndex === 0}
              onClick={() => onChange(activeIndex - 1)}
              className="absolute left-3 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label="Next clip"
              disabled={activeIndex === clips.length - 1}
              onClick={() => onChange(activeIndex + 1)}
              className="absolute right-3 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex min-h-0 flex-col overflow-y-auto p-5 sm:p-7">
            <DialogTitle className="pr-8 text-2xl leading-tight text-navy">{title}</DialogTitle>
            <DialogDescription className="mt-2">{clip.business?.name ?? 'HubHive Shop'}</DialogDescription>
            {clip.product ? (
              <div className="mt-7 border-y border-border py-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-secondary">
                    {productImage && !productImageFailed ? (
                      // Shop image proxy — not an optimizer-allowlisted host.
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={productImage}
                        alt=""
                        onError={() => setProductImageFailed(true)}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <ShoppingBag className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-navy">{clip.product.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">Tagged product</p>
                  </div>
                </div>
                <Button asChild className="mt-4 w-full rounded-full">
                  <ShopLink href={`${clip.product.productUrl}?clip=${encodeURIComponent(clip.id)}`}>
                    View product in Shop <ExternalLink className="h-4 w-4" />
                  </ShopLink>
                </Button>
              </div>
            ) : (
              <div className="mt-7 flex items-center gap-3 border-y border-border py-5">
                <Store className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-semibold text-navy">{clip.business?.name ?? 'HubHive Shop'}</p>
                  <p className="text-sm text-muted-foreground">Featured store</p>
                </div>
              </div>
            )}
            {clip.business && (
              <Button asChild variant="outline" className="mt-4 w-full rounded-full">
                <ShopLink href={routes.shopSite.store(clip.business.slug)}>
                  Visit store <ExternalLink className="h-4 w-4" />
                </ShopLink>
              </Button>
            )}
            <ShopLink href={clip.clipUrl} className="mt-auto pt-7 text-center text-sm font-semibold text-primary hover:underline">
              Watch this clip in Shop
            </ShopLink>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
